import type { Sale, SaleAdditionalItem, SaleOffer } from '../api/SalesAPI';

const SCALE = 2;

export type { SaleOffer };

export function additionalItemLineTotal(item: SaleAdditionalItem): number {
  return parseFloat(
    ((item.linePrice ?? 0) * Math.max(item.quantity ?? 1, 1)).toFixed(SCALE)
  );
}

export function formatOfferSummary(
  offer: SaleOffer,
  currency: string = 'EUR',
  _baseAmount?: number
): string {
  const concept = offer.concept.trim();
  const discountPart =
    offer.discountType === 'PERCENT'
      ? `−${offer.discountValue}%`
      : `−${(offer.discountValue ?? 0).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} ${currency}`;
  return `${concept} · ${discountPart}`;
}

export function isOfferPresent(offer?: SaleOffer | null): boolean {
  return !!(
    offer?.concept?.trim() &&
    offer.discountType &&
    offer.discountValue != null &&
    !Number.isNaN(offer.discountValue)
  );
}

export function discountAmount(base: number, offer?: SaleOffer | null): number {
  const safeBase = parseFloat((base ?? 0).toFixed(SCALE));
  if (!isOfferPresent(offer) || offer!.discountValue == null) {
    return 0;
  }
  let discount =
    offer!.discountType === 'PERCENT'
      ? safeBase * (offer!.discountValue / 100)
      : offer!.discountValue;
  discount = parseFloat(discount.toFixed(SCALE));
  if (discount > safeBase) return safeBase;
  if (discount < 0) return 0;
  return discount;
}

export function applyOffer(base: number, offer?: SaleOffer | null): number {
  const safeBase = parseFloat((base ?? 0).toFixed(SCALE));
  if (!isOfferPresent(offer)) return safeBase;
  return parseFloat(Math.max(0, safeBase - discountAmount(safeBase, offer)).toFixed(SCALE));
}

function grossLineTotals(sale: {
  primaryLinePrice?: number;
  totalPrice?: number;
  additionalItems?: SaleAdditionalItem[];
}): number[] {
  const primary = sale.primaryLinePrice ?? sale.totalPrice ?? 0;
  const lines = [primary];
  for (const item of sale.additionalItems ?? []) {
    lines.push((item.linePrice ?? 0) * Math.max(item.quantity ?? 1, 1));
  }
  return lines.map((v) => parseFloat(v.toFixed(SCALE)));
}

function allocateSaleOfferNetLines(grossLines: number[], saleNetTotal: number): number[] {
  const safeNet = parseFloat(saleNetTotal.toFixed(SCALE));
  if (grossLines.length === 0) return [safeNet];
  const grossTotal = grossLines.reduce((a, b) => a + b, 0);
  if (grossTotal <= 0) return grossLines.map(() => 0);

  const netLines: number[] = [];
  let allocated = 0;
  for (let i = 0; i < grossLines.length; i++) {
    let net: number;
    if (i === grossLines.length - 1) {
      net = parseFloat((safeNet - allocated).toFixed(SCALE));
    } else {
      net = parseFloat(((safeNet * grossLines[i]!) / grossTotal).toFixed(SCALE));
      allocated = parseFloat((allocated + net).toFixed(SCALE));
    }
    netLines.push(Math.max(0, net));
  }
  return netLines;
}

export function netLineTotals(sale: {
  primaryLinePrice?: number;
  totalPrice?: number;
  additionalItems?: SaleAdditionalItem[];
  saleOffer?: SaleOffer;
  primaryLineOffer?: SaleOffer;
}): number[] {
  const grossLines = grossLineTotals(sale);
  if (isOfferPresent(sale.saleOffer)) {
    const grossSubtotal = grossLines.reduce((a, b) => a + b, 0);
    const netTotal = applyOffer(grossSubtotal, sale.saleOffer);
    return allocateSaleOfferNetLines(grossLines, netTotal);
  }
  const nets = [applyOffer(grossLines[0] ?? 0, sale.primaryLineOffer)];
  for (let i = 1; i < grossLines.length; i++) {
    const item = sale.additionalItems?.[i - 1];
    nets.push(applyOffer(grossLines[i]!, item?.offer));
  }
  return nets;
}

export function computeSaleTotal(sale: {
  primaryLinePrice?: number;
  totalPrice?: number;
  additionalItems?: SaleAdditionalItem[];
  saleOffer?: SaleOffer;
  primaryLineOffer?: SaleOffer;
}): number {
  return parseFloat(
    netLineTotals(sale)
      .reduce((sum, line) => sum + line, 0)
      .toFixed(SCALE)
  );
}

export function computeGrossSaleSubtotal(sale: {
  primaryLinePrice?: number;
  totalPrice?: number;
  additionalItems?: SaleAdditionalItem[];
}): number {
  return parseFloat(
    grossLineTotals(sale)
      .reduce((sum, line) => sum + line, 0)
      .toFixed(SCALE)
  );
}

export function hasSaleLevelOffer(sale: { saleOffer?: SaleOffer }): boolean {
  return isOfferPresent(sale.saleOffer);
}

export function hasAnyLineLevelOffer(sale: {
  primaryLineOffer?: SaleOffer;
  additionalItems?: SaleAdditionalItem[];
}): boolean {
  if (isOfferPresent(sale.primaryLineOffer)) return true;
  return (sale.additionalItems ?? []).some((item) => isOfferPresent(item.offer));
}

export function hasAnyDiscount(sale: {
  saleOffer?: SaleOffer;
  primaryLineOffer?: SaleOffer;
  additionalItems?: SaleAdditionalItem[];
}): boolean {
  return hasSaleLevelOffer(sale) || hasAnyLineLevelOffer(sale);
}

export type SaleDiscountTooltipLine = {
  label?: string;
  summary: string;
  saleLevel?: boolean;
};

function formatAdditionalItemDiscountLabel(item: SaleAdditionalItem): string {
  const name = item.itemName;
  const variant = item.productVariant ? ` (${item.productVariant})` : '';
  const quantity = item.quantity ?? 1;
  const quantitySuffix = quantity > 1 ? ` (×${quantity})` : '';
  return `${name}${variant}${quantitySuffix}`;
}

export function getSaleDiscountTooltipLines(sale: {
  saleOffer?: SaleOffer;
  primaryLineOffer?: SaleOffer;
  additionalItems?: SaleAdditionalItem[];
  currency: string;
  dressName: string;
}): SaleDiscountTooltipLine[] {
  if (hasSaleLevelOffer(sale)) {
    return [
      {
        saleLevel: true,
        summary: formatOfferSummary(sale.saleOffer!, sale.currency),
      },
    ];
  }

  const lines: SaleDiscountTooltipLine[] = [];
  if (isOfferPresent(sale.primaryLineOffer)) {
    lines.push({
      label: sale.dressName,
      summary: formatOfferSummary(sale.primaryLineOffer!, sale.currency),
    });
  }
  for (const item of sale.additionalItems ?? []) {
    if (isOfferPresent(item.offer)) {
      lines.push({
        label: formatAdditionalItemDiscountLabel(item),
        summary: formatOfferSummary(item.offer!, sale.currency),
      });
    }
  }
  return lines;
}

export type SaleLineDiscountBreakdownLine = {
  label: string;
  summary: string;
  grossAmount: number;
  netAmount: number;
};

export function getLineLevelDiscountBreakdown(sale: {
  dressName: string;
  primaryLinePrice?: number;
  totalPrice?: number;
  primaryLineOffer?: SaleOffer;
  additionalItems?: SaleAdditionalItem[];
  currency: string;
}): SaleLineDiscountBreakdownLine[] {
  if (!hasAnyLineLevelOffer(sale)) {
    return [];
  }

  const grossLines = grossLineTotals(sale);
  const netLines = netLineTotals(sale);
  const lines: SaleLineDiscountBreakdownLine[] = [];

  lines.push({
    label: sale.dressName,
    summary: isOfferPresent(sale.primaryLineOffer)
      ? formatOfferSummary(sale.primaryLineOffer!, sale.currency)
      : '',
    grossAmount: grossLines[0] ?? 0,
    netAmount: netLines[0] ?? 0,
  });

  for (let i = 0; i < (sale.additionalItems ?? []).length; i++) {
    const item = sale.additionalItems![i]!;
    lines.push({
      label: formatAdditionalItemDiscountLabel(item),
      summary: isOfferPresent(item.offer) ? formatOfferSummary(item.offer!, sale.currency) : '',
      grossAmount: grossLines[i + 1] ?? 0,
      netAmount: netLines[i + 1] ?? 0,
    });
  }

  return lines;
}

export function clearLineLevelOffers(sale: Sale): Partial<Sale> {
  const additionalItems = (sale.additionalItems ?? []).map((item) => ({ ...item, offer: undefined }));
  const next = { ...sale, primaryLineOffer: undefined, additionalItems };
  return {
    primaryLineOffer: undefined,
    additionalItems,
    totalPrice: computeSaleTotal(next),
  };
}

export function clearSaleLevelOffer(sale: Sale): Partial<Sale> {
  const next = { ...sale, saleOffer: undefined };
  return {
    saleOffer: undefined,
    totalPrice: computeSaleTotal(next),
  };
}

export function applySaleLevelOffer(sale: Sale, offer: SaleOffer | undefined): Partial<Sale> {
  if (!isOfferPresent(offer)) {
    return withPricingRecalc(sale, { saleOffer: undefined });
  }
  const cleared = clearLineLevelOffers(sale);
  return withPricingRecalc(
    { ...sale, ...cleared, saleOffer: offer },
    { ...cleared, saleOffer: offer }
  );
}

export function applyPrimaryLineOffer(sale: Sale, offer: SaleOffer | undefined): Partial<Sale> {
  if (!isOfferPresent(offer)) {
    return withPricingRecalc(sale, { primaryLineOffer: undefined });
  }
  const clearedSaleLevel = clearSaleLevelOffer(sale);
  return withPricingRecalc(
    { ...sale, ...clearedSaleLevel, primaryLineOffer: offer },
    { ...clearedSaleLevel, primaryLineOffer: offer }
  );
}

export function applyAdditionalItemOffer(
  sale: Sale,
  index: number,
  offer: SaleOffer | undefined
): Partial<Sale> {
  const items = sale.additionalItems ?? [];
  const nextItems = items.map((row, i) =>
    i === index ? { ...row, offer: isOfferPresent(offer) ? offer : undefined } : row
  );
  if (isOfferPresent(offer)) {
    const cleared = clearSaleLevelOffer(sale);
    return withPricingRecalc(
      { ...sale, ...cleared, additionalItems: nextItems },
      { ...cleared, additionalItems: nextItems }
    );
  }
  return withPricingRecalc(sale, { additionalItems: nextItems });
}

export function withPricingRecalc(sale: Sale, updates: Partial<Sale>): Partial<Sale> {
  const merged = { ...sale, ...updates };
  return { ...updates, totalPrice: computeSaleTotal(merged) };
}

export function normalizeSalePricing(sale: Sale): Sale {
  const primaryLinePrice = sale.primaryLinePrice ?? sale.totalPrice;
  const additionalItems = sale.additionalItems ?? [];
  return {
    ...sale,
    primaryLinePrice,
    additionalItems,
    totalPrice: computeSaleTotal({ ...sale, primaryLinePrice, additionalItems }),
  };
}

export function withPrimaryLinePrice(sale: Sale, primaryLinePrice: number): Partial<Sale> {
  return withPricingRecalc(sale, { primaryLinePrice });
}

export function saleNeedsInternalProduction(sale: Sale): boolean {
  if (sale.productId && !sale.orderId && isInternalSale(sale)) {
    return true;
  }
  return (sale.additionalItems ?? []).some(
    (item) => item.productId && !item.orderId && item.b2bType !== 'OUT'
  );
}

export function saleNeedsExternalOrder(sale: Sale): boolean {
  if (sale.b2bType === 'OUT' && sale.productId && !sale.externalOrderId) {
    return true;
  }
  return (sale.additionalItems ?? []).some(
    (item) => item.b2bType === 'OUT' && item.productId && !item.externalOrderId
  );
}

export function linkedProductionOrderIds(sale: Sale): number[] {
  const ids: number[] = [];
  if (sale.orderId) ids.push(sale.orderId);
  for (const item of sale.additionalItems ?? []) {
    if (item.orderId) ids.push(item.orderId);
  }
  return ids;
}

export function linkedExternalOrderIds(sale: Sale): number[] {
  const ids: number[] = [];
  if (sale.externalOrderId) ids.push(sale.externalOrderId);
  for (const item of sale.additionalItems ?? []) {
    if (item.externalOrderId) ids.push(item.externalOrderId);
  }
  return ids;
}

function isInternalSale(sale: { b2bType?: string }): boolean {
  return sale.b2bType !== 'OUT';
}
